import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { GlobalAlertService } from '../shared/services/global-alert.service';
import { ServiceDeliveryPointService } from '../shared/services/service-delivery-points.service';
import { NgxDeeplinkerService } from 'ngx-deeplinker';
import { MatDialog } from '@angular/material/dialog';
import { DeleteDialogComponent } from '../dialogs/delete/delete-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { ServiceDeliveryPoint } from '../shared/model/service-delivery-point.model';
import { Locale } from '../shared/model/locale.model';
import { OrganizationsService } from '../shared/services/organizations.service';
import { SiteService } from '../shared/services/sites.service';
import { lastValueFrom } from 'rxjs';
import { ServiceDeliveryPointFormComponent } from '../service_delivery_point_form/service-delivery-point-form.component';
import { ServiceAccountPost } from '../shared/model/service-account.model';
import { ExternalPointsService, UtilityAccountsService } from '@services';
import { ExternalPointPost } from '../shared/model/external-point.model';
import { buildExternalPointPayloadFromForm } from '../shared/mappers/external-point.mapper';
import { ContextSelectorService } from 'ngx-global-nav';

@Component({
  selector: 'app-edit',
  templateUrl: './edit.component.html',
  styleUrls: ['./edit.component.scss', '../shared/shared.styles.scss'],
})
export class EditComponent implements OnInit, OnDestroy {
  SUCCESS = 'Created Successfully';
  BAD_REQUEST = 'Oops, There was a problem with your request';
  NOT_UPDATED = 'Oops, There was a problem updating your sdp';
  REQUIRED = 'required';
  CONFLICT = 'There is a data conflict with this ServiceDeliveryPoint';
  SERVICE_ACCOUNT_ERROR = 'One or more service accounts failed to save';
  EXTERNAL_POINTS_ERROR = 'One or more external points failed to save';

  serviceDeliveryPointId = '';
  subscriptions = [];
  isSubmitting = false;
  isLoadingAsset = false;
  selectedSite = null;
  currentSite = null;
  serviceDeliveryPointForm: FormGroup = new FormGroup({
    id: new FormControl('', Validators.required),
    displayLabels: new FormControl('', Validators.required),
    supportedLocales: new FormControl([new Locale()]),
    descriptions: new FormControl(''),
    defaultLocale: new FormControl('', Validators.required),
    timezone: new FormControl('America/New_York', Validators.required),
    status: new FormControl('ACTIVE', Validators.required),
    siteId: new FormControl('', Validators.required),
    commodity: new FormControl('', Validators.required),
    startDttm: new FormControl(new Date(), Validators.required),
    endDttm: new FormControl(null),
    sdpType: new FormControl('UTILITY', Validators.required),
    utilityMeterNumber: new FormControl(''),
  });
  readonly mode = 'edit';
  readonly APPPREFIX = 'prt';
  serviceDeliveryPoint: ServiceDeliveryPoint;

  @ViewChild('sdpForm') sdpForm: ServiceDeliveryPointFormComponent;
  orgId: string;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private messageService: GlobalAlertService,
    private serviceDeliveryPointService: ServiceDeliveryPointService,
    private ngxDeeplinkerService: NgxDeeplinkerService,
    private translateService: TranslateService,
    private organizationsService: OrganizationsService,
    public dialog: MatDialog,
    private siteService: SiteService,
    private utilityAccountsService: UtilityAccountsService,
    private externalPointsService: ExternalPointsService,
    private cdr: ChangeDetectorRef,
    public orgSelectorService: ContextSelectorService,
  ) {
    if (this.organizationsService.isInit) {
      this.organizationsService.getOrgs();
    }
    this.translateService
      .get('service.delivery.account.notification.updated_successfully')
      .subscribe((result: string) => {
        this.SUCCESS = result;
        this.BAD_REQUEST = this.translateService.instant('service.delivery.account.notification.bad_request');
        this.NOT_UPDATED = this.translateService.instant('service.delivery.account.notification.not_updated');
        this.REQUIRED = this.translateService.instant('service.delivery.account.validation.required');
        this.CONFLICT = this.translateService.instant('service.delivery.account.notification.conflict');
      });

    this.serviceDeliveryPointService.conflictError.subscribe(() => {
      this.messageService.setError(this.CONFLICT, 7000);
    });
  }

  ngOnInit() {
    const routeSub = this.route.params.subscribe(async (params) => {
      if (params.id) {
        this.serviceDeliveryPointId = params.id;
        this.isLoadingAsset = true;
        this.serviceDeliveryPointService.selectedServiceDeliveryPointId$.next(this.serviceDeliveryPointId);
        await this.serviceDeliveryPointService.getServiceDeliveryPoint(params.id);
        this.isLoadingAsset = false;
        this.siteService.setIsFirst(true);
      }
    });

    const serviceDeliveryPointSub = this.serviceDeliveryPointService.serviceDeliveryPoint$.subscribe(
      async (serviceDeliveryPoint) => {
        if (serviceDeliveryPoint) {
          this.serviceDeliveryPoint = serviceDeliveryPoint;
          this.serviceDeliveryPoint.commodity = this.serviceDeliveryPoint.commodity.toUpperCase();
          this.serviceDeliveryPointForm.patchValue(serviceDeliveryPoint);
          await this.serviceDeliveryPointService.getSDPServiceAccounts(serviceDeliveryPoint.id);
        }
      },
    );

    const selectedSiteSub = this.siteService.selectedSite$.subscribe(async (selectedSite) => {
      if (selectedSite) {
        this.selectedSite = selectedSite;
        this.serviceDeliveryPointForm.patchValue(this.serviceDeliveryPoint);
        this.serviceDeliveryPointService.setSdpParentId(selectedSite);
        this.serviceDeliveryPointService.selectSDP(this.serviceDeliveryPointId);
      }
    });

    this.subscriptions = [routeSub, serviceDeliveryPointSub, selectedSiteSub];
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }

  async handleSubmit() {
    if (!this.serviceDeliveryPointForm.valid) {
      this.messageService.setError(this.REQUIRED);
    } else {
      try {
        let sdpTransformBody;
        let currentSite;
        this.isSubmitting = true;
        const sdp = this.serviceDeliveryPointForm.getRawValue();
        const siteId = sdp.siteId;
        const parent = this.serviceDeliveryPointService.getSdpParentId();
        const sitesSub = this.siteService.sites$.subscribe((sites) => {
          currentSite = sites.filter((site) => site.id === siteId);
        });
        sdpTransformBody = {
          ...sdp,
          address1: currentSite[0].address1,
          address2: currentSite[0].address2,
          city: currentSite[0].city,
          name: sdp.displayLabels[sdp.defaultLocale],
          country: currentSite[0].country,
          state: currentSite[0].state,
          timezone: currentSite[0].timezone,
        };
        if (!!currentSite[0].postalCode) {
          sdpTransformBody.postal_code = currentSite[0]?.postalCode?.toString();
        }
        const response = await this.serviceDeliveryPointService.updateServiceDeliveryPoint(
          { ...sdpTransformBody },
          siteId,
          parent,
        );
        const serviceAccountErrors = await this.submitServiceAccounts();
        if (serviceAccountErrors.length > 0) {
          throw new Error('ServiceAccountError');
        }
        const externalPointsErrors = await this.submitExternalPoints();
        if (externalPointsErrors.length > 0) {
          throw new Error('ExternalPointsError');
        }
        this.ngxDeeplinkerService.returnHandler({ appPrefix: this.APPPREFIX, callbackValue: response.id });
        this.messageService.setSuccess(this.SUCCESS);
        try {
          await this.router.navigate([`details/${response.id}/view`], {});
        } catch (refreshError) {
          console.error('Error refreshing SDP list:', refreshError);
          this.siteService.selectSite(siteId);
          await this.router.navigate([`details/${response.id}/view`], {});
        }

        this.subscriptions.push(sitesSub);
      } catch (e) {
        console.log(e);

        const message = e?.message;

        if (message === 'ExternalPointsError') {
          this.messageService.setError(this.EXTERNAL_POINTS_ERROR);
          return;
        }

        if (message === 'ServiceAccountError') {
          this.messageService.setError(this.SERVICE_ACCOUNT_ERROR);
          return;
        }

        const fallbackMessage = e?.error?.message ?? 'ERR_BAD_REQUEST';
        this.messageService.setError(this.NOT_UPDATED);
        if (fallbackMessage !== 'ERR_BAD_REQUEST') {
          console.warn('Unexpected error message:', fallbackMessage);
        }
      } finally {
        this.isSubmitting = false;
      }
    }
  }

  private getServiceAccountBaseData(): { userLocale: string; siteId: string; timezone: string } {
    return {
      userLocale: this.sdpForm.userLocale,
      siteId: this.serviceDeliveryPointForm.get('siteId').value,
      timezone: this.serviceDeliveryPointForm.get('timezone').value,
    };
  }
  private getExternalPointsBaseData(): {
    timezone: string;
    userLocale: string;
    serviceDeliveryPointId: string;
  } {
    return {
      timezone: this.serviceDeliveryPointForm.get('timezone').value,
      userLocale: this.sdpForm.userLocale,
      serviceDeliveryPointId: this.serviceDeliveryPointId,
    };
  }

  private createDisplayLabels(serviceAccountNumber: string, userLocale: string): Record<string, string> {
    const displayLabels = { [userLocale]: serviceAccountNumber };
    if (userLocale !== 'en_US') {
      displayLabels['en_US'] = serviceAccountNumber;
    }
    return displayLabels;
  }

  private mapFormToServiceAccount(
    accountForm: FormGroup,
    baseData: { userLocale: string; siteId: string; timezone: string },
  ): ServiceAccountPost {
    const { userLocale, siteId, timezone } = baseData;
    const serviceAccountNumber = accountForm.get('serviceAccountNumber').value;

    return {
      utility_name: accountForm.get('utility').value.name,
      display_labels: this.createDisplayLabels(serviceAccountNumber, userLocale),
      service_delivery_point_id: this.serviceDeliveryPointId,
      site_id: siteId,
      timezone: timezone,
      service_account_number: serviceAccountNumber,
      ecrm_id: accountForm.get('ecrmId').value || null,
      space_type: 'ServiceAccount',
      start_dttm: accountForm.get('startDate').value.toISOString(),
      end_dttm: accountForm.get('endDate').value?.toISOString() || null,
      status: accountForm.getRawValue().status,
    };
  }

  private mapFormToExternalPoints(
    pointForm: FormGroup,
    baseData: { timezone: string; userLocale: string; serviceDeliveryPointId: string },
  ): ExternalPointPost {
    return buildExternalPointPayloadFromForm(pointForm, baseData, this.sdpForm.reportingIntervals);
  }

  private async processUpdates(
    updates: Array<{ id: string; index: number; payload: ServiceAccountPost }>,
  ): Promise<Error[]> {
    const errors: Error[] = [];
    for (const { id, index, payload } of updates) {
      const serviceAccountForm = this.sdpForm.serviceAccountForms.get(index);
      try {
        await this.utilityAccountsService.updateServiceAccount(id, payload);
        serviceAccountForm.markAsPristine({ onlySelf: false });
        serviceAccountForm.markAsUntouched({ onlySelf: false });
      } catch (e) {
        errors.push(e);
        serviceAccountForm.markAsTouched({ onlySelf: false });
        serviceAccountForm.markAsDirty({ onlySelf: false });
        this.markFormWithError('serviceAccount', index);
      }
    }
    return errors;
  }

  private async processCreates(creates: Array<{ index: number; payload: ServiceAccountPost }>): Promise<Error[]> {
    const errors: Error[] = [];
    for (const { index, payload } of creates) {
      const serviceAccountForm = this.sdpForm.serviceAccountForms.get(index);
      try {
        await this.utilityAccountsService.createServiceAccount(payload);
        serviceAccountForm.markAsPristine({ onlySelf: false });
        serviceAccountForm.markAsUntouched({ onlySelf: false });
      } catch (e) {
        errors.push(e);
        serviceAccountForm.markAsTouched({ onlySelf: false });
        serviceAccountForm.markAsDirty({ onlySelf: false });
        this.markFormWithError('serviceAccount', index);
      }
    }
    return errors;
  }

  private markFormWithError(formType: string, index: number) {
    if (formType === 'serviceAccount') {
      return this.sdpForm.flagServiceAccountError(index);
    } else {
      return this.sdpForm.flagExternalPointError(index);
    }
  }

  private async processExternalPointsUpdates(
    updates: Array<{ id: string; index: number; payload: ExternalPointPost }>,
  ): Promise<Error[]> {
    const errors: Error[] = [];
    for (const { id, index, payload } of updates) {
      const pointForm = this.sdpForm.externalPointForms.get(index);
      try {
        await this.externalPointsService.updateExternalPoint(id, payload);
        pointForm.markAsPristine({ onlySelf: false });
        pointForm.markAsUntouched({ onlySelf: false });
      } catch (err) {
        errors.push(err);
        pointForm.markAsTouched({ onlySelf: false });
        pointForm.markAsDirty({ onlySelf: false });
        this.markFormWithError('externalPoint', index);
      }
    }
    return errors;
  }

  private async processExternalPointsCreates(
    creates: Array<{ index: number; payload: ExternalPointPost }>,
  ): Promise<Error[]> {
    const errors: Error[] = [];

    for (const { index, payload } of creates) {
      const pointForm = this.sdpForm.externalPointForms.get(index);
      try {
        await this.externalPointsService.createExternalPoint(payload);
        pointForm.markAsPristine({ onlySelf: false });
        pointForm.markAsUntouched({ onlySelf: false });
      } catch (err) {
        errors.push(err);
        pointForm.markAsTouched({ onlySelf: false });
        pointForm.markAsDirty({ onlySelf: false });
        this.markFormWithError('externalPoint', index);
      }
    }
    return errors;
  }

  async submitServiceAccounts(): Promise<Error[]> {
    const baseData = this.getServiceAccountBaseData();
    const errors: Error[] = [];
    const toPut: Array<{ id: string; index: number; payload: ServiceAccountPost }> = [];
    const toPost: Array<{ index: number; payload: ServiceAccountPost }> = [];
    for (let i = 0; i < this.sdpForm.serviceAccounts.length; i++) {
      const account = this.sdpForm.serviceAccounts[i];
      const hasServerError = account.hasServerError;
      this.sdpForm.clearServiceAccountError(i);

      const accountForm = this.sdpForm.serviceAccountForms.get(i);
      if (!accountForm) {
        errors.push(new Error('ServiceAccountError'));
        continue;
      }
      if (accountForm && (!accountForm.pristine || hasServerError)) {
        const serviceAccountPost = this.mapFormToServiceAccount(accountForm, baseData);
        if (account.id) {
          toPut.push({ id: account.id, index: i, payload: serviceAccountPost });
        } else {
          toPost.push({ index: i, payload: serviceAccountPost });
        }
      }
    }
    const updateErrors = await this.processUpdates(toPut);
    if (updateErrors.length === 0) {
      const createErrors = await this.processCreates(toPost);
      errors.push(...createErrors);
    } else {
      errors.push(...updateErrors);
    }
    return errors;
  }

  async submitExternalPoints(): Promise<Error[]> {
    const baseData = this.getExternalPointsBaseData();
    const errors: Error[] = [];
    const toPut: Array<{ id: string; index: number; payload: ExternalPointPost }> = [];
    const toPost: Array<{ index: number; payload: ExternalPointPost }> = [];
    for (let i = 0; i < this.sdpForm.externalPoints.length; i++) {
      const point = this.sdpForm.externalPoints[i];
      const hasServerError = point.hasServerError;
      this.sdpForm.clearExternalPointError(i);

      const pointForm = this.sdpForm.externalPointForms.get(i);
      if (!pointForm) {
        errors.push(new Error('ExternalPointsError'));
        continue;
      }

      if (pointForm && (!pointForm.pristine || hasServerError)) {
        const externalPointsPost = this.mapFormToExternalPoints(pointForm, baseData);
        if (point.id) {
          toPut.push({ id: point.id, index: i, payload: externalPointsPost });
        } else {
          toPost.push({ index: i, payload: externalPointsPost });
        }
      }
    }
    const updateErrors = await this.processExternalPointsUpdates(toPut);
    if (updateErrors.length === 0) {
      const createErrors = await this.processExternalPointsCreates(toPost);
      errors.push(...createErrors);
    } else {
      errors.push(...updateErrors);
    }
    return errors;
  }

  openDeleteDialog(): void {
    this.dialog.open(DeleteDialogComponent, {
      width: '400px',
      data: {
        assetId: this.serviceDeliveryPointId,
      },
    });
  }

  handleCancel() {
    this.router.navigate([`details/${this.serviceDeliveryPointId}/view`]);
  }
}
